/* CascadeValueEditor.js — admin modal for managing the structured-
 * column enum sets that drive Component Rate Tables (Ridge material,
 * Ridge class, etc.).
 *
 * Pattern (Q1c): the same component is reused both in-context (a
 * `Manage` button on the filter-pill row → modal opens scoped to
 * that one (component, field) pair) and in the master overview page
 * `/settings/cascade-values` (one editor per entry).
 *
 * Delete UX (Q2c): if the value is still in use, the API returns
 * `{needs_remap:true, affected_rows:N, available_values:[…]}`. We
 * then show a remap picker and call DELETE again with `?remap_to=`.
 */
import React, { useEffect, useState } from 'react';
import { X, Plus, Pencil, Trash2, Check, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function CascadeValueEditor({ component, field, open, onClose, onChange }) {
  const { api } = useAuth();
  const [doc, setDoc] = useState({ values: [], aliases: {} });
  const [loading, setLoading] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [editingValue, setEditingValue] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [removeState, setRemoveState] = useState(null);  // {value, affected, options}

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/cascade-values/${component}/${field}`)
      .then(({ data }) => setDoc({ values: data.values || [], aliases: data.aliases || {} }))
      .catch((e) => toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false));
  }, [open, component, field]);

  if (!open) return null;

  const refresh = async () => {
    const { data } = await api.get(`/cascade-values/${component}/${field}`);
    setDoc({ values: data.values || [], aliases: data.aliases || {} });
    onChange?.();
  };

  const handleAdd = async () => {
    const v = newValue.trim();
    if (!v) return;
    try {
      await api.post(`/cascade-values/${component}/${field}`, { value: v });
      toast.success(`Added "${v}"`);
      setNewValue('');
      await refresh();
    } catch (e) {
      toast.error(`Add failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleStartEdit = (v) => { setEditingValue(v); setEditingDraft(v); };
  const handleSaveEdit = async () => {
    const nv = editingDraft.trim();
    if (!nv || nv === editingValue) { setEditingValue(null); return; }
    try {
      // Use body-based PUT so values containing "/" (e.g. Valley
      // profile "300G/3F") aren't mis-routed by URL decoding.
      const { data } = await api.put(`/cascade-values/${component}/${field}`, { old_value: editingValue, new_value: nv });
      toast.success(`Renamed → "${nv}" (${data.rows_remapped || 0} row${data.rows_remapped === 1 ? '' : 's'} re-tagged)`);
      setEditingValue(null);
      await refresh();
    } catch (e) {
      toast.error(`Rename failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleDelete = async (v) => {
    // First attempt — no remap target. If the API blocks (needs_remap)
    // we open the remap picker. Uses query-param `value` so "/" inside
    // the value doesn't break path routing.
    try {
      const { data } = await api.delete(`/cascade-values/${component}/${field}`, { params: { value: v } });
      if (data.needs_remap) {
        setRemoveState({ value: v, affected: data.affected_rows, options: data.available_values });
      } else {
        toast.success(`Removed "${v}"`);
        await refresh();
      }
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleConfirmRemap = async (remapTo) => {
    try {
      const { data } = await api.delete(`/cascade-values/${component}/${field}`, { params: { value: removeState.value, remap_to: remapTo } });
      toast.success(`Removed "${removeState.value}" — ${data.rows_remapped || 0} row${data.rows_remapped === 1 ? '' : 's'} re-tagged to "${remapTo}".`);
      setRemoveState(null);
      await refresh();
    } catch (e) {
      toast.error(`Remap failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div data-testid={`cascade-value-editor-${component}-${field}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Manage values</div>
            <h3 className="text-lg font-semibold text-slate-900 capitalize">{component} <span className="text-slate-400">·</span> {field}</h3>
          </div>
          <button data-testid="cascade-value-editor-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {removeState && (
          <div className="px-5 py-4 bg-amber-50 border-b border-amber-200">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>{removeState.affected}</strong> row{removeState.affected === 1 ? '' : 's'} still use <code className="px-1 py-0.5 bg-white rounded font-mono text-xs">{removeState.value}</code>. Pick a replacement value to remap them to:
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {removeState.options.map(opt => (
                <button
                  key={opt}
                  data-testid={`cascade-remap-pick-${opt}`}
                  onClick={() => handleConfirmRemap(opt)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 transition-all"
                >
                  Remap → {opt}
                </button>
              ))}
              <button onClick={() => setRemoveState(null)} className="px-3 py-1.5 rounded-full text-xs font-medium text-slate-600 hover:bg-slate-100">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="p-5 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <ul className="space-y-1.5" data-testid={`cascade-value-list-${field}`}>
              {doc.values.map((v) => (
                <li key={v} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                  {editingValue === v ? (
                    <>
                      <input
                        data-testid={`cascade-edit-input-${v}`}
                        autoFocus
                        value={editingDraft}
                        onChange={(e) => setEditingDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditingValue(null); }}
                        className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm bg-white"
                      />
                      <button data-testid={`cascade-save-edit-${v}`} onClick={handleSaveEdit} className="p-1 rounded hover:bg-emerald-100 text-emerald-600">
                        <Check size={14} />
                      </button>
                      <button onClick={() => setEditingValue(null)} className="p-1 rounded hover:bg-slate-200 text-slate-500">
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm font-medium text-slate-800" data-testid={`cascade-value-item-${v}`}>{v}</span>
                      <button data-testid={`cascade-rename-${v}`} onClick={() => handleStartEdit(v)} className="p-1 rounded hover:bg-sky-100 text-sky-600" title="Rename (cascades to all rows)">
                        <Pencil size={13} />
                      </button>
                      <button data-testid={`cascade-delete-${v}`} onClick={() => handleDelete(v)} className="p-1 rounded hover:bg-rose-100 text-rose-600" title="Delete (with remap)">
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </li>
              ))}
              {doc.values.length === 0 && (
                <li className="text-sm text-slate-400 italic">No values yet. Add the first one below.</li>
              )}
            </ul>
          )}
          {Object.keys(doc.aliases || {}).length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Legacy aliases (auto-canonicalise to their target)</div>
              <ul className="space-y-1">
                {Object.entries(doc.aliases).map(([alias, target]) => (
                  <li key={alias} className="text-xs text-slate-500 flex items-center gap-2">
                    <code className="px-1.5 py-0.5 bg-slate-100 rounded font-mono">{alias}</code>
                    <span>→</span>
                    <code className="px-1.5 py-0.5 bg-slate-100 rounded font-mono">{target}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2">
          <input
            data-testid={`cascade-add-input-${field}`}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            placeholder={`New ${field}…`}
            className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
          />
          <button
            data-testid={`cascade-add-btn-${field}`}
            onClick={handleAdd}
            disabled={!newValue.trim()}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#D35400] text-white hover:bg-[#B7480C] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    </div>
  );
}
